import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
} from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import SelectApplication from "components/SelectApplication";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import Field from "components/Field";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  SaveSettingValueRequest,
  SettingCache,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import { SettingTypeId } from "types/common-types";
import { prepareSettingValue, validateSettingValue } from "./settingValueUtils";
import SettingValueField from "./SettingValueField";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
};

type ApplicationOverrideFormProps = {
  setting: SettingCache;
  override?: SettingCache;
  existingApplicationIds?: string[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const ApplicationOverrideForm = ({
  setting,
  override,
  existingApplicationIds = [],
  open,
  onClose,
  onSaved,
}: ApplicationOverrideFormProps) => {
  const { formatMessage: t } = useIntl();
  const { settingService } = useGlobalContext();
  const isEditing = !!override;
  const [confirmOpen, confirmOpenActions] = useBoolean(false);
  const [
    pendingValues,
    setPendingValues,
  ] = useState<SaveSettingValueRequest | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);

  const init = {
    applicationId: override?.applicationId,
    value:
      override?.value ??
      (setting.settingTypeId === SettingTypeId.Bool ? "false" : ""),
  };

  const validateForm = (values: SaveSettingValueRequest) => {
    const errors: any = {};
    const typeError = validateSettingValue(setting, values, t);
    if (typeError) {
      errors.value = typeError;
    }
    return errors;
  };

  const handleSubmit = (values: SaveSettingValueRequest) => {
    const request = SaveSettingValueRequest.fromJS({
      id: override?.settingValueId,
      settingId: setting.settingId,
      applicationId: values.applicationId,
      value: prepareSettingValue(values.value, setting.settingTypeId),
    });
    setBackendError(null);
    setPendingValues(request);
    confirmOpenActions.setTrue();
  };

  const onConfirm = async () => {
    try {
      await settingService.saveSettingValue(pendingValues);
      confirmOpenActions.setFalse();
      onClose();
      onSaved();
    } catch (err: any) {
      confirmOpenActions.setFalse();
      setBackendError(translateError(err, t));
    } finally {
      setPendingValues(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <T
          id={
            isEditing
              ? "editApplicationOverride"
              : "addApplicationOverride"
          }
        />
      </DialogTitle>
      <Form
        onSubmit={handleSubmit}
        initialValues={init}
        validate={validateForm}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent dividers>
              <Box mb={2}>
                <Field name="applicationId" required>
                  {({ input, meta }) => (
                    <FormControl
                      fullWidth
                      error={!!(meta.error && meta.touched)}
                    >
                      <SelectApplication
                        value={input.value || undefined}
                        onChange={input.onChange}
                        label={t({ id: "applicationLabel" })}
                        disabled={isEditing}
                        required
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                          required: true,
                        }}
                        excludeApplicationIds={
                          isEditing
                            ? existingApplicationIds.filter((id) => id !== override?.applicationId)
                            : existingApplicationIds
                        }
                      />
                      {meta.error && meta.touched && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Field>
              </Box>
              <Box>
                <SettingValueField
                  settingTypeId={setting?.settingTypeId}
                  settingOptions={setting?.settingOptions}
                />
              </Box>
              {(submitError || backendError) && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError || backendError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={submitting}>
                <T id="cancelButton" />
              </Button>
              <Button type="submit" color="primary" disabled={submitting}>
                <T id="saveButton" />
              </Button>
            </DialogActions>
          </form>
        )}
      />
      <ConfirmDialog
        dialogTitleKey="areYouSure"
        rawContent={t(
          { id: "settingSaveConfirmationText" },
          { organization: setting?.organizationName! },
        )}
        open={confirmOpen}
        onCancel={confirmOpenActions.setFalse}
        handleConfirm={onConfirm}
      />
    </Dialog>
  );
};

export default ApplicationOverrideForm;
